import React, { useRef, useState, useEffect, TextareaHTMLAttributes } from 'react'

interface AutosizeDescriptionProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange'> {
    value: string
    onChange: (value: string) => void
    maxChars?: number
    collapseOnBlur?: boolean
    warningThreshold?: number
    baseHeight?: number
    maxHeight?: number
    counterInside?: boolean
    showCounter?: boolean
}

const AutosizeDescription: React.FC<AutosizeDescriptionProps> = ({
    value,
    onChange,
    maxChars = 500,
    collapseOnBlur = true,
    warningThreshold = 450,
    baseHeight = 38,
    maxHeight = 240,
    counterInside = false,
    showCounter = false,
    placeholder = 'Enter description (optional)',
    className = '',
    ...rest
}) => {
    const ref = useRef<HTMLTextAreaElement | null>(null)
    const [focused, setFocused] = useState(false)

    const autoResize = () => {
        const el = ref.current
        if (!el) return
        el.style.height = 'auto'
        const newHeight = Math.min(el.scrollHeight, maxHeight)
        el.style.height = newHeight + 'px'
    }

    useEffect(() => { autoResize() }, [value])

    const handleBlur = () => {
        setFocused(false)
        if (collapseOnBlur) {
            const el = ref.current
            if (el) el.style.height = baseHeight + 'px'
        }
    }

    const counter = (
        <span
            className={`pointer-events-none absolute bottom-1.5 right-2 text-[10px] font-medium transition-colors ${value.length > warningThreshold ? 'text-red-500' : 'text-gray-400'
                }`}
        >
            {value.length}/{maxChars}
        </span>
    )

    return (
        <div className={`relative group ${focused ? 'z-10' : ''} notranslate`}>
            <textarea
                ref={ref}
                value={value}
                maxLength={maxChars}
                onChange={(e) => onChange(e.target.value)}
                onFocus={() => {
                    setFocused(true)
                    requestAnimationFrame(autoResize)
                }}
                onBlur={handleBlur}
                rows={1}
                placeholder={placeholder}
                aria-expanded={focused}
                style={{ minHeight: baseHeight, background: '#ffffff' }}
                className={`w-full resize-none overflow-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-50 px-2 py-1.5 text-sm border rounded outline-none transition-all duration-200 bg-white text-gray-900 placeholder:text-gray-400 shadow-sm ${focused ? 'border-brand-500 ring-1 ring-brand-500' : 'border-gray-300 focus:border-brand-500 focus:ring-1 focus:ring-brand-500'
                    } ${className}`}
                {...rest}
            />
            {counterInside && showCounter && counter}
        </div>
    )
}

export default AutosizeDescription
